import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import Autocomplete from "@mui/material/Autocomplete";
import { DropdownChevron } from "component/shared/dropdown-chevron";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { getAgentsWithLeads, getPostalCodes, getLeadsOptimized } from "redux/selector";
import { Agent } from "model/agent";
import { GeographicRoute, NewGeographicRoute } from "model/geographic-route";
import { baseColors } from "theme";

const useStyles = makeStyles()((theme: Mui.Theme) => ({
	chipDeleteIcon: { color: theme.palette.text.primary, width: 16, height: 16 },
	agentPill: { backgroundColor: baseColors.teal25, color: baseColors.slateTeal },
	// Align buttons + close icon to the shared "Unsaved changes" modal (MAX-3831 review):
	// #5F6368 close icon, 8px action gap, both actions 36px tall, 2px secondary border.
	closeButton: { color: theme.palette.custom.textSecondary },
	actions: { gap: 8, padding: "8px 24px 24px" },
	secondaryButton: {
		height: 36,
		boxSizing: "border-box",
		borderWidth: 2,
		borderRadius: 8,
		"&:hover": { borderWidth: 2 },
	},
	primaryButton: {
		height: 36,
		boxSizing: "border-box",
		border: "2px solid transparent",
		borderRadius: 8,
	},
}));

// Force the Autocomplete dropdown to always open below the input (disable flip-to-top).
const BottomPopper = (props: any) => (
	<Mui.Popper {...props} placement="bottom-start" modifiers={{ flip: { enabled: false } }} />
);

interface Props {
	open: boolean;
	agentRoutes: { agent: Agent; routes: GeographicRoute[] }[];
	// When provided, the modal opens in edit mode: agent is locked and the agent's
	// existing postal codes are pre-selected. Same modal as "Add Agent", just pre-filled.
	editAgentRoute?: { agent: Agent; routes: GeographicRoute[] } | null;
	onClose: () => void;
	onSave: (geographicRoute: NewGeographicRoute) => void;
	onDelete?: (geographicRoute: GeographicRoute) => void;
}

const SELECT_ALL = "__select_all__";

// Refresh "Add Agent for Geographic Routing" modal (gated behind refreshDesignsDev).
export const AddAgentDialog: React.FC<Props> = ({ open, agentRoutes, editAgentRoute, onClose, onSave, onDelete }) => {
	const { classes } = useStyles();
	const agents = useSelector((state: RootState) => getAgentsWithLeads(state));
	const postalCodes = useSelector((state: RootState) => getPostalCodes(state));
	const leads = useSelector((state: RootState) => getLeadsOptimized(state));

	const [agent, setAgent] = useState<Agent | null>(null);
	const [selected, setSelected] = useState<string[]>([]);

	// Seed the form when the dialog opens: pre-fill with the agent's existing data in edit
	// mode, or start empty for a fresh "Add Agent".
	useEffect(() => {
		if (!open) return;
		if (editAgentRoute) {
			setAgent(editAgentRoute.agent);
			setSelected(editAgentRoute.routes.map((r) => r.postalCode));
		} else {
			setAgent(null);
			setSelected([]);
		}
	}, [open, editAgentRoute]);

	// Postal codes already assigned to another agent can't be reassigned. In edit mode the
	// agent's own codes are excluded so they stay selectable/removable.
	const assigned = useMemo(
		() => agentRoutes
			.filter((ar) => !editAgentRoute || ar.agent.id !== editAgentRoute.agent.id)
			.flatMap((ar) => ar.routes.map((r) => r.postalCode)),
		[agentRoutes, editAgentRoute]
	);
	// Postal codes available to assign: the curated /postal-codes.json set unioned with the
	// distinct zips that actually appear on the client's leads (plus the agent's existing
	// codes in edit mode, so none disappear from the picker).
	const options = useMemo(() => {
		const set = new Set<string>();
		postalCodes.forEach((p) => { if (p?.postalCode) set.add(p.postalCode.trim()); });
		leads.forEach((l) => { const z = (l.zip || "").trim(); if (z) set.add(z); });
		if (editAgentRoute) editAgentRoute.routes.forEach((r) => set.add(r.postalCode));
		return Array.from(set).sort();
	}, [postalCodes, leads, editAgentRoute]);
	const selectable = useMemo(() => options.filter((o) => !assigned.includes(o)), [options, assigned]);
	const allSelected = selectable.length > 0 && selectable.every((o) => selected.includes(o));
	const someSelected = selected.length > 0 && !allSelected;

	const reset = () => {
		setAgent(null);
		setSelected([]);
	};
	const close = () => {
		reset();
		onClose();
	};
	const save = () => {
		if (!agent || selected.length === 0) return;
		if (editAgentRoute) {
			// Reconcile against the agent's existing codes: add the new ones, delete the removed ones.
			const existing = editAgentRoute.routes.map((r) => r.postalCode);
			selected
				.filter((postalCode) => !existing.includes(postalCode))
				.forEach((postalCode) => onSave({ agent, postalCode }));
			editAgentRoute.routes
				.filter((route) => !selected.includes(route.postalCode))
				.forEach((route) => onDelete?.(route));
		} else {
			selected.forEach((postalCode) => onSave({ agent, postalCode }));
		}
		close();
	};

	return (
		<Mui.Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
			<Mui.DialogTitle>
				<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
					<Mui.Box display="flex" alignItems="center" style={{ gap: 12 }}>
						<Mui.Typography variant="h6" style={{ fontWeight: "bold", fontSize: 24 }}>
							{editAgentRoute ? "Edit Postal Codes" : "Add Agent for Geographic Routing"}
						</Mui.Typography>
						{editAgentRoute && agent && <Mui.Chip label={agent.name} className={classes.agentPill} />}
					</Mui.Box>
					<Mui.IconButton size="small" className={classes.closeButton} onClick={close}>
						<Icons.X size={24} />
					</Mui.IconButton>
				</Mui.Box>
			</Mui.DialogTitle>
			<Mui.Divider style={{ marginLeft: 24, marginRight: 24 }} />
			<Mui.DialogContent style={{ padding: "16px 24px 16px" }}>
				{!editAgentRoute && (
					<>
						<Mui.Typography variant="body1" style={{ marginBottom: 16 }}>
							Select an agent and assign one or more postal codes. Leads on listings within those postal codes will be routed to the selected agent.
						</Mui.Typography>

						<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>Agent</Mui.Typography>
						<Autocomplete
							options={agents}
							getOptionLabel={(a) => a.name || ""}
							value={agent}
							onChange={(event, value) => setAgent(value)}
							popupIcon={<DropdownChevron />}
							PopperComponent={BottomPopper}
							renderInput={(params) => (
								<Mui.TextField {...params} variant="outlined" placeholder="Select an agent" />
							)}
						/>
					</>
				)}

				<Mui.Typography variant="body2" color="textSecondary" style={{ margin: editAgentRoute ? "0 0 8px" : "16px 0 8px" }}>Postal Codes</Mui.Typography>
				<Autocomplete
					multiple
					disableCloseOnSelect
					options={[SELECT_ALL, ...options]}
					value={selected}
					getOptionDisabled={(o) => o !== SELECT_ALL && assigned.includes(o)}
					getOptionLabel={(o) => (o === SELECT_ALL ? "Select All" : o)}
					popupIcon={<DropdownChevron />}
					PopperComponent={BottomPopper}
					onChange={(event, values) => {
						const arr = values as string[];
						if (arr.includes(SELECT_ALL)) {
							setSelected(allSelected ? [] : selectable);
						} else {
							setSelected(arr);
						}
					}}
					renderOption={(props, option, { selected: isSelected }) => (
						<li {...props}>
							<Mui.Checkbox
								color="secondary"
								style={{ marginRight: 8 }}
								checked={option === SELECT_ALL ? allSelected : isSelected}
								indeterminate={option === SELECT_ALL ? someSelected : undefined}
							/>
							{option === SELECT_ALL ? <strong>Select All</strong> : option}
						</li>
					)}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => {
							const { key, ...tagProps } = getTagProps({ index });
							return (
								<Mui.Chip
									key={key}
									label={option}
									deleteIcon={<Icons.X size={12} />}
									classes={{ deleteIcon: classes.chipDeleteIcon }}
									{...tagProps}
								/>
							);
						})
					}
					renderInput={(params) => (
						<Mui.TextField {...params} variant="outlined" />
					)}
				/>
			</Mui.DialogContent>
			<Mui.DialogActions className={classes.actions} disableSpacing>
				<Mui.Button className={classes.secondaryButton} variant="outlined" color="primary" onClick={close}>
					Cancel
				</Mui.Button>
				<Mui.Button className={classes.primaryButton} variant="contained" color="primary" disabled={!agent || selected.length === 0} onClick={save}>
					{editAgentRoute ? "Save" : "Add Agent"}
				</Mui.Button>
			</Mui.DialogActions>
		</Mui.Dialog>
	);
};
